import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, api } from '../context/AuthContext';
import { 
  Sparkles, LogOut, Home, Activity, Users, BarChart3, MessageSquare,
  Trash2, RefreshCw, Shield, Clock, UserCheck, Bot, Flag, StickyNote,
  X, Check, Filter
} from 'lucide-react';

export default function AdminPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('stats');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [chatLogs, setChatLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showFlaggedOnly, setShowFlaggedOnly] = useState(false);
  const [flaggedCount, setFlaggedCount] = useState(0);
  const [editingNote, setEditingNote] = useState(null);
  const [noteText, setNoteText] = useState('');

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/tracker');
      return;
    }
    loadData();
  }, [user, navigate]);

  const loadChatLogs = React.useCallback(async () => {
    try {
      const res = await api.get(`/api/admin/chat-logs?limit=50&flagged_only=${showFlaggedOnly}`);
      setChatLogs(res.data.logs);
    } catch (err) {
      console.error('Failed to load chat logs:', err);
    }
  }, [showFlaggedOnly]);

  useEffect(() => {
    if (activeTab === 'logs') {
      loadChatLogs();
    }
  }, [showFlaggedOnly, activeTab, loadChatLogs]);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [statsRes, usersRes, logsRes, flaggedRes] = await Promise.all([
        api.get('/api/admin/stats'),
        api.get('/api/admin/users'),
        api.get('/api/admin/chat-logs?limit=50'),
        api.get('/api/admin/flagged-count')
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users);
      setChatLogs(logsRes.data.logs);
      setFlaggedCount(flaggedRes.data.flagged_count);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const deleteUser = async (userId, email) => {
    if (!window.confirm(`Delete user ${email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/api/admin/users/${userId}`);
      setUsers(users.filter(u => u._id !== userId));
      setStats(prev => ({ ...prev, total_users: prev.total_users - 1, user_count: prev.user_count - 1 }));
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const toggleFlag = async (logId, currentFlagged) => {
    try {
      await api.patch(`/api/admin/chat-logs/${logId}`, { flagged: !currentFlagged });
      setChatLogs(logs => logs.map(log => 
        log._id === logId ? { ...log, flagged: !currentFlagged } : log
      ));
      setFlaggedCount(prev => currentFlagged ? prev - 1 : prev + 1);
    } catch (err) {
      alert('Failed to update flag');
    }
  };

  const saveNote = async (logId) => {
    try {
      await api.patch(`/api/admin/chat-logs/${logId}`, { admin_note: noteText });
      setChatLogs(logs => logs.map(log => 
        log._id === logId ? { ...log, admin_note: noteText, noted_by: user.email } : log
      ));
      setEditingNote(null);
      setNoteText('');
    } catch (err) {
      alert('Failed to save note');
    }
  };

  const startEditNote = (log) => {
    setEditingNote(log._id);
    setNoteText(log.admin_note || '');
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  if (user?.role !== 'admin') {
    return null;
  }

  return (
    <div className="bg-hikari min-h-screen">
      <div className="orb orb-mint"></div>
      <div className="orb orb-violet"></div>

      <div className="content-wrapper">
        {/* Header */}
        <header className="glass mx-4 md:mx-6 mt-4 px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#FBBF24] to-[#F472B6] flex items-center justify-center">
              <Shield className="w-4 h-4 text-[#0A0B10]" />
            </div>
            <div>
              <div className="kicker text-[#FBBF24] text-[10px]">Hikari Health</div>
              <div className="text-sm font-semibold">Admin Dashboard</div>
            </div>
          </div>
          
          <nav className="flex items-center gap-2">
            <Link to="/" className="p-2 rounded-lg hover:bg-white/5 text-[#94A3B8] hover:text-white" data-testid="nav-home">
              <Home className="w-5 h-5" />
            </Link>
            <Link to="/tracker" className="p-2 rounded-lg hover:bg-white/5 text-[#94A3B8] hover:text-white" data-testid="nav-tracker">
              <Activity className="w-5 h-5" />
            </Link>
            <button onClick={handleLogout} className="p-2 rounded-lg hover:bg-white/5 text-[#94A3B8] hover:text-white" data-testid="logout-button">
              <LogOut className="w-5 h-5" />
            </button>
          </nav>
        </header>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          {/* Page Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <div className="kicker text-[#FBBF24] mb-2">Admin Panel</div>
              <h1 className="font-display text-3xl md:text-4xl font-light">Behind the Scenes</h1>
              <p className="text-[#94A3B8] mt-2 text-sm">
                Manage users, view analytics, and monitor AI interactions
              </p>
            </div>
            <button 
              onClick={loadData} 
              className="btn-secondary py-2 px-4 text-sm flex items-center gap-2"
              disabled={loading}
              data-testid="refresh-button"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </div>

          {error && (
            <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Tab Navigation */}
          <div className="flex gap-2 mb-6">
            {[
              { id: 'stats', label: 'Analytics', icon: BarChart3 },
              { id: 'users', label: 'Users', icon: Users },
              { id: 'logs', label: 'AI Chat Logs', icon: MessageSquare, badge: flaggedCount }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors relative ${
                  activeTab === tab.id 
                    ? 'bg-[#FBBF24]/10 border border-[#FBBF24]/20 text-[#FBBF24]' 
                    : 'bg-white/5 border border-white/5 text-[#94A3B8] hover:bg-white/10'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
                {tab.badge > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center">
                    {tab.badge}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Stats Tab */}
          {activeTab === 'stats' && stats && (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="glass-card p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-[#2DD4BF]/20 flex items-center justify-center">
                    <Users className="w-5 h-5 text-[#2DD4BF]" />
                  </div>
                  <div className="kicker text-[#94A3B8]">Total Users</div>
                </div>
                <div className="text-3xl font-semibold" data-testid="stat-total-users">{stats.total_users}</div>
                <div className="text-sm text-[#94A3B8] mt-1">{stats.admin_count} admins, {stats.user_count} users</div>
              </div>

              <div className="glass-card p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-[#8B5CF6]/20 flex items-center justify-center">
                    <UserCheck className="w-5 h-5 text-[#8B5CF6]" />
                  </div>
                  <div className="kicker text-[#94A3B8]">New This Week</div>
                </div>
                <div className="text-3xl font-semibold" data-testid="stat-recent-users">{stats.recent_registrations}</div>
                <div className="text-sm text-[#94A3B8] mt-1">Last 7 days</div>
              </div>

              <div className="glass-card p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-[#3B82F6]/20 flex items-center justify-center">
                    <Bot className="w-5 h-5 text-[#3B82F6]" />
                  </div>
                  <div className="kicker text-[#94A3B8]">AI Messages</div>
                </div>
                <div className="text-3xl font-semibold" data-testid="stat-chat-messages">{stats.total_chat_messages}</div>
                <div className="text-sm text-[#94A3B8] mt-1">Total interactions</div>
              </div>

              <div className="glass-card p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-[#F472B6]/20 flex items-center justify-center">
                    <Flag className="w-5 h-5 text-[#F472B6]" />
                  </div>
                  <div className="kicker text-[#94A3B8]">Flagged</div>
                </div>
                <div className="text-3xl font-semibold" data-testid="stat-flagged">{flaggedCount}</div>
                <div className="text-sm text-[#94A3B8] mt-1">Needs review</div>
              </div>
            </div>
          )}

          {/* Users Tab */}
          {activeTab === 'users' && (
            <div className="glass-card overflow-hidden">
              <div className="p-4 border-b border-white/5">
                <h2 className="font-semibold">Registered Users ({users.length})</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/5 text-left text-sm text-[#94A3B8]">
                      <th className="p-4">Name</th>
                      <th className="p-4">Email</th>
                      <th className="p-4">Role</th>
                      <th className="p-4">Joined</th>
                      <th className="p-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(u => (
                      <tr key={u._id} className="border-b border-white/5 hover:bg-white/3" data-testid={`user-row-${u._id}`}>
                        <td className="p-4 font-medium">{u.name}</td>
                        <td className="p-4 text-[#94A3B8]">{u.email}</td>
                        <td className="p-4">
                          <span className={`pathway-pill ${u.role === 'admin' ? 'gold' : 'mint'}`}>
                            {u.role}
                          </span>
                        </td>
                        <td className="p-4 text-[#94A3B8] text-sm">{formatDate(u.created_at)}</td>
                        <td className="p-4">
                          {u.role !== 'admin' && (
                            <button 
                              onClick={() => deleteUser(u._id, u.email)}
                              className="p-2 rounded-lg hover:bg-red-500/20 text-red-400"
                              data-testid={`delete-user-${u._id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Chat Logs Tab */}
          {activeTab === 'logs' && (
            <div className="glass-card overflow-hidden">
              <div className="p-4 border-b border-white/5 flex items-center justify-between">
                <h2 className="font-semibold">AI Conversations ({chatLogs.length})</h2>
                <button
                  onClick={() => setShowFlaggedOnly(!showFlaggedOnly)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                    showFlaggedOnly 
                      ? 'bg-[#F472B6]/20 text-[#F472B6] border border-[#F472B6]/30' 
                      : 'bg-white/5 text-[#94A3B8] hover:bg-white/10'
                  }`}
                  data-testid="filter-flagged"
                >
                  <Filter className="w-4 h-4" />
                  {showFlaggedOnly ? 'Showing Flagged' : 'Show Flagged Only'}
                </button>
              </div>
              {chatLogs.length === 0 ? (
                <div className="p-8 text-center text-[#94A3B8]">
                  <Bot className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>{showFlaggedOnly ? 'No flagged conversations.' : 'No AI chat logs yet.'}</p>
                  <p className="text-sm mt-2">
                    {showFlaggedOnly 
                      ? 'Flag conversations to track them here.' 
                      : 'Chat logs will appear here when users interact with the AI Tutor.'}
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-white/5">
                  {chatLogs.map(log => (
                    <div key={log._id} className={`p-4 hover:bg-white/3 ${log.flagged ? 'border-l-2 border-l-[#F472B6]' : ''}`} data-testid={`chat-log-${log._id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{log.user_name}</span>
                          <span className="text-[#94A3B8] text-sm">({log.user_email})</span>
                          <span className="pathway-pill mint text-[10px]">Lesson {log.lesson_index + 1}</span>
                          {log.flagged && (
                            <span className="pathway-pill pink text-[10px] flex items-center gap-1">
                              <Flag className="w-3 h-3" /> Flagged
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-[#475569]">{formatDate(log.timestamp)}</span>
                          <button
                            onClick={() => toggleFlag(log._id, log.flagged)}
                            className={`p-1.5 rounded-lg transition-colors ${
                              log.flagged 
                                ? 'bg-[#F472B6]/20 text-[#F472B6]' 
                                : 'hover:bg-white/10 text-[#94A3B8]'
                            }`}
                            title={log.flagged ? 'Remove flag' : 'Flag for review'}
                            data-testid={`flag-${log._id}`}
                          >
                            <Flag className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => startEditNote(log)}
                            className={`p-1.5 rounded-lg transition-colors ${
                              log.admin_note 
                                ? 'bg-[#FBBF24]/20 text-[#FBBF24]' 
                                : 'hover:bg-white/10 text-[#94A3B8]'
                            }`}
                            title="Add/edit note"
                            data-testid={`note-${log._id}`}
                          >
                            <StickyNote className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      
                      <div className="grid md:grid-cols-2 gap-4 mt-3">
                        <div className="glass-subtle p-3 rounded-xl">
                          <div className="text-xs text-[#94A3B8] mb-1">User asked:</div>
                          <div className="text-sm">{log.user_message}</div>
                        </div>
                        <div className="glass-subtle p-3 rounded-xl">
                          <div className="text-xs text-[#2DD4BF] mb-1">AI responded:</div>
                          <div className="text-sm text-[#94A3B8]">{log.ai_response}...</div>
                        </div>
                      </div>

                      {/* Admin Note Display/Edit */}
                      {editingNote === log._id ? (
                        <div className="mt-3 p-3 rounded-xl bg-[#FBBF24]/5 border border-[#FBBF24]/20">
                          <div className="text-xs text-[#FBBF24] mb-2 flex items-center gap-1">
                            <StickyNote className="w-3 h-3" /> Admin Note
                          </div>
                          <textarea
                            value={noteText}
                            onChange={(e) => setNoteText(e.target.value)}
                            placeholder="Add your notes about this conversation..."
                            className="w-full h-20 text-sm resize-none"
                            autoFocus
                            data-testid={`note-input-${log._id}`}
                          />
                          <div className="flex justify-end gap-2 mt-2">
                            <button
                              onClick={() => { setEditingNote(null); setNoteText(''); }}
                              className="p-2 rounded-lg hover:bg-white/10 text-[#94A3B8]"
                            >
                              <X className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => saveNote(log._id)}
                              className="p-2 rounded-lg bg-[#FBBF24]/20 text-[#FBBF24] hover:bg-[#FBBF24]/30"
                              data-testid={`save-note-${log._id}`}
                            >
                              <Check className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ) : log.admin_note && (
                        <div className="mt-3 p-3 rounded-xl bg-[#FBBF24]/5 border border-[#FBBF24]/20">
                          <div className="text-xs text-[#FBBF24] mb-1 flex items-center gap-1">
                            <StickyNote className="w-3 h-3" /> Note by {log.noted_by}
                          </div>
                          <div className="text-sm">{log.admin_note}</div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
